import { useState } from "react";
import { Loader2, MessageSquare, Plus, Target } from "lucide-react";
import { useTranslation } from "react-i18next";
import StudioFromGoalDialog from "@/components/studio/StudioFromGoalDialog";
import { formatStudioThreadOptionLabel } from "@/components/studio/studioThreadUtils";
import { useAgentSpecStudioThreads } from "@/hooks/useAgentSpecStudioThreads";
import { cn } from "@/utils/cn";

export default function AgentSpecStudioThreadsSidebar({
  expanded,
  onNavigate,
}: {
  expanded: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const [fromGoalOpen, setFromGoalOpen] = useState(false);
  const {
    canWriteRecipes,
    organizationId,
    agentId,
    threads,
    threadsQuery,
    selectedThreadId,
    createThread,
    createThreadFromGoal,
    createThreadPending,
    createFromGoalPending,
    selectThread,
    listError,
  } = useAgentSpecStudioThreads();
  const createBusy =
    createThreadPending || createFromGoalPending || threadsQuery.isLoading;

  if (!expanded) return null;
  if (!canWriteRecipes || !organizationId || !agentId) return null;

  return (
    <div className="ml-3 mt-2 space-y-1 border-l border-sidebar-border pl-3">
      <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-sidebar-foreground/50">
        {t("agentSpec.studioThreadsTitle")}
      </p>

      <button
        type="button"
        onClick={() => {
          createThread();
          onNavigate?.();
        }}
        disabled={createBusy}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          !selectedThreadId
            ? "bg-agent-violet/10 font-medium text-agent-violet"
            : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-foreground",
          createBusy && "pointer-events-none opacity-60",
        )}
      >
        {createThreadPending ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin motion-reduce:animate-none" />
        ) : (
          <Plus className="h-3.5 w-3.5 shrink-0" />
        )}
        <span className="truncate">
          {t(
            createThreadPending
              ? "agentSpec.studioCreatingThread"
              : "agentSpec.studioCreateThread",
          )}
        </span>
      </button>

      <button
        type="button"
        onClick={() => setFromGoalOpen(true)}
        disabled={createBusy}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-foreground",
          createBusy && "pointer-events-none opacity-60",
        )}
      >
        {createFromGoalPending ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin motion-reduce:animate-none" />
        ) : (
          <Target className="h-3.5 w-3.5 shrink-0" />
        )}
        <span className="truncate">{t("agentSpec.studioFromGoalAction")}</span>
      </button>

      <StudioFromGoalDialog
        open={fromGoalOpen}
        pending={createFromGoalPending}
        onOpenChange={setFromGoalOpen}
        onSubmit={(goal) => {
          createThreadFromGoal(goal, {
            onSuccess: () => {
              setFromGoalOpen(false);
              onNavigate?.();
            },
          });
        }}
      />

      {threadsQuery.isLoading ? (
        <div className="flex items-center gap-2 px-2 py-2 text-xs text-sidebar-foreground/50">
          <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
          {t("agentSpec.studioThreadsLoading")}
        </div>
      ) : null}

      {!threadsQuery.isLoading && threads.length === 0 ? (
        <p className="px-2 py-1 text-xs text-sidebar-foreground/50">
          {t("agentSpec.studioNoThreadYet")}
        </p>
      ) : null}

      {listError ? (
        <p className="px-2 py-1 text-xs text-destructive" role="alert">
          {listError}
        </p>
      ) : null}

      <div className="max-h-64 space-y-1 overflow-y-auto">
        {threads.map((thread) => {
          const active = thread.id === selectedThreadId;
          const label = formatStudioThreadOptionLabel(thread, threads);
          return (
            <button
              key={thread.id}
              type="button"
              onClick={() => {
                selectThread(thread.id);
                onNavigate?.();
              }}
              aria-current={active ? "true" : undefined}
              title={label}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active
                  ? "bg-sidebar-accent font-medium text-foreground"
                  : "text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-foreground",
              )}
            >
              <MessageSquare className="h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
